import { BusinessResult } from '../../../business/models/policy-business.model';
import { CommentOrReport } from '../comment-or-report/model';
import { HonoraryNote } from '../honorary-note/types';
import { Image } from '../images/model';
import { OtherAttachment } from '../other-attachments/model';
import { RimTotal } from '../rim/types';
import { SvsItem } from '../svs/types';
import { TypeCodeTranslation } from '../../../business/models/shared.model';

export enum communicationOverviewStatus {
  open = 'open',
  sent = 'sent'
}

export enum DocumentTypeTC {
  image = 'Image',
  expertiseAttachment = 'ExpertiseAttachment',
  otherAttachment = 'OtherAttachment',
  commentOrReport = 'CommentOrReport',
  rimReport = 'RimReport',
  svsReport = 'SvsReport',
  daccaCalculationRIK = 'DaccaCalculationRIK',
  daccaCalculationDIY = 'DaccaCalculationDIY',
  daccaCalculationFIN = 'DaccaCalculationFIN'
}

export interface CommunicationOverviewRimItem {
  readonly id: number;
  readonly status: communicationOverviewStatus;
  readonly sentOn: Date;
}

export interface CommunicationOverviewSvsItem {
  readonly id: number;
  readonly sentOn: Date;
  readonly status: communicationOverviewStatus;
}

export interface CommunicationOverviewDocumentItem {
  id: number;
  sentOn: Date;
  status: communicationOverviewStatus;
  documentType: DocumentTypeTC;
  reportStatusTC: string;
}

export interface CommunicationOverviewHonoraryNoteItem {
  id: number;
  status: communicationOverviewStatus;
  messageTypeTC: string;
  sent: boolean;
  sentOn: Date;
  amount: number;
}

export class CommunicationOverviewRimItemForDisplay {
  constructor(item: CommunicationOverviewRimItem,
             rimTotal: RimTotal,
             rimStatusList: TypeCodeTranslation[],
             selected: boolean,
             isValid: boolean) {
    this._item = item;
    this._rimTotal = rimTotal;
    this._selected = selected;
    this._isValid = isValid;
    this._rimStatusList = rimStatusList;
  }

  private _item: CommunicationOverviewRimItem;
  private _rimTotal: RimTotal;
  private _selected: boolean;
  private _isValid: boolean;
  private _rimStatusList: TypeCodeTranslation[];

  id = () => this._rimTotal.rimHeaderId;
  isValid = () => this._isValid;
  date = () => this._rimTotal.rimData.createdOn;
  sentOn = () => this._item ? this._item.sentOn : '';
  selected = () => this._selected;
  status = () => this._item ? this._item.status : communicationOverviewStatus.open;
  canSend = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  canDelete = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  amazonKey = () => this._rimTotal.rimData.amazonKey;
  daccaDiyAmazonKey = () => this._rimTotal.rimData.daccaDiyAmazonKey;
  daccaFinAmazonKey = () => this._rimTotal.rimData.daccaFinAmazonKey;
  rimStatus = () => this._rimTotal.rimData.rimStatusTC;
  rimStatusList = () => this._rimStatusList ? this._rimStatusList : [];
  createdByName = () => this._rimTotal.rimData.createdByFirstName + ' ' + this._rimTotal.rimData.createdByLastName;
  needsApproval = () => this._rimTotal.rimData.approved === false;
}

export class CommunicationOverviewSvsItemForDisplay {
  constructor(item: CommunicationOverviewSvsItem, svs: SvsItem, selected: boolean, isValid: boolean) {
    this._item = item;
    this._svs = svs;
    this._selected = selected;
    this._isValid = isValid;
  }

  private _item: CommunicationOverviewSvsItem;
  private _svs: SvsItem;
  private _selected: boolean;
  private _isValid: boolean;

  id = () => this._svs.svsData.id;
  isValid = () => this._isValid;
  date = () => this._svs.svsData.createdOn;
  sentOn = () => this._item ? this._item.sentOn : '';
  selected = () => this._selected;
  status = () => this._item ? this._item.status : communicationOverviewStatus.open;
  canSend = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  canDelete = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  documentAmazonKey = () => this._svs.svsData.documentAmazonKey;
  daccaRikAmazonKey = () => this._svs.svsData.daccaRikAmazonKey;
  createdByName = () => this._svs.svsData.createdByFirstName + ' ' + this._svs.svsData.createdByLastName;
  needsApproval = () => this._svs.svsData.approved === false;
}

export class CommunicationOverviewDocumentItemForDisplay {
  constructor(item: CommunicationOverviewDocumentItem, doc: Image | OtherAttachment, selected: boolean) {
    this._item = item;
    this._doc = doc;
    this._selected = selected;
  }

  private _item: CommunicationOverviewDocumentItem;
  private _doc: Image | OtherAttachment;
  private _selected: boolean;

  id = () => this._doc.id;
  sentOn = () => this._item ? this._item.sentOn : '';
  selected = () => this._selected;
  status = () => this._item ? this._item.status : communicationOverviewStatus.open;
  canSend = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  canDelete = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  amazonKey = () => this._doc.amazonKey;
  bucketName = () => this._doc.bucketName;
  url = () => this._doc.url;
  content = () => this._doc.content;
  filename = () => this._doc.filename;
  displayText = () => {
    if (this._doc.documentType === DocumentTypeTC.image || this._doc.documentType === DocumentTypeTC.otherAttachment) {

      if (this._doc.description) {
        if (this._doc.description.length > 50) {
          return this._doc.description.substr(0, 50).concat('...');
        }
        return this._doc.description;
      }

      if (this._doc.filename.length > 50) {
        return this._doc.filename.substr(0, 50).concat('...');
      }
      return this._doc.filename;
    }

    // for 'FreeText' and 'CommentOrReport'
    return this._doc.subjectLine;
  }
  createdByName = () => this._doc.createdByFirstName + ' ' + this._doc.createdByLastName;
  needsApproval = () => this._doc.approved === false;
  virusDetected = () => this._doc.VirusDetected === true;
  isScanning = () => this._doc.AVScanned === false;
  queuedForSending = () => this._doc.hasBeenQueued === true && !this._doc.AVScanned
}

export class CommunicationOverviewFreeTextItemForDisplay {
  constructor(item: CommunicationOverviewDocumentItem, commentOrReport: CommentOrReport, selected: boolean) {
    this._item = item;
    this._commentOrReport = commentOrReport;
    this._selected = selected;
  }

  private _item: CommunicationOverviewDocumentItem;
  private _commentOrReport: CommentOrReport;
  private _selected: boolean;

  id = () => this._commentOrReport.id;
  sentOn = () => this._item ? this._item.sentOn : '';
  selected = () => this._selected;
  status = () => this._item ? this._item.status : communicationOverviewStatus.open;
  canSend = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  canDelete = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  subjectLine = () => this._commentOrReport.subjectLine;
  needsApproval = () => this._commentOrReport.approved === false;
  createdByName = () => this._commentOrReport.createdByFirstName + ' ' + this._commentOrReport.createdByLastName;
}

export class CommunicationOverviewReportItemForDisplay {
  constructor(item: CommunicationOverviewDocumentItem, commentOrReport: CommentOrReport, reportStatusList: TypeCodeTranslation[], selected: boolean, language: string) {
    this._item = item;
    this._commentOrReport = commentOrReport;
    this._reportStatusList = reportStatusList;
    this._selected = selected;
    this._language = language;
  }

  private _item: CommunicationOverviewDocumentItem;
  private _commentOrReport: CommentOrReport;
  private _reportStatusList: TypeCodeTranslation[];
  private _selected: boolean;
  private _language: string;

  id = () => this._commentOrReport.id;
  sentOn = () => this._item ? this._item.sentOn : '';
  selected = () => this._selected;
  status = () => this._item ? this._item.status : communicationOverviewStatus.open;
  canSend = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  canDelete = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  amazonKey = () => this._commentOrReport.amazonKey;
  url = () => this._commentOrReport.url;
  content = () => this._commentOrReport.content;
  filename = () => this._commentOrReport.filename;
  subjectLine = () => this._commentOrReport.subjectLine;

  reportType = () => {
    if (this._reportStatusList !== undefined) {
      const reportTypeForTranslation = this._reportStatusList.filter(x => x.language === this._language && x.typeCodeValue === this._commentOrReport.reportStatusTC);
      return reportTypeForTranslation[0] ? reportTypeForTranslation[0].translation : '';
    } else {
      return '';
    }
  }
  createdByName = () => this._commentOrReport.createdByFirstName + ' ' + this._commentOrReport.createdByLastName;
  needsApproval = () => this._commentOrReport.approved === false;
  virusDetected = () => this._commentOrReport.VirusDetected === true;
  isScanning = () => this._commentOrReport.AVScanned === false;
  queuedForSending = () => this._commentOrReport.hasBeenQueued === true && !this._commentOrReport.AVScanned
}

export class CommunicationOverviewHonoraryNoteItemForDisplay {
  constructor(item: CommunicationOverviewHonoraryNoteItem, honoraryNote: HonoraryNote, selected: boolean, amount: number) {
    this._item = item;
    this._honoraryNote = honoraryNote;
    this._selected = selected;
    this._amount = amount;
  }

  private _item: CommunicationOverviewHonoraryNoteItem;
  private _honoraryNote: HonoraryNote;
  private _selected: boolean;
  private _amount: number;

  id = () => this._honoraryNote.honoraryNoteId;
  date = () => this._honoraryNote.createdOn;
  sentOn = () => this._item ? this._item.sentOn : '';
  selected = () => this._selected;
  status = () => this._item ? this._item.status : communicationOverviewStatus.open;
  canSend = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  canDelete = () => this._item ? this._item.status === communicationOverviewStatus.open : true;
  amount = () => this._amount;
  createdByName = () => this._honoraryNote.createdByFirstName + ' ' + this._honoraryNote.createdByLastName;
  needsApproval = () => this._honoraryNote.approved === false;
}

export interface OverviewState {
  readonly rimList: CommunicationOverviewRimItem[];
  readonly svsList: CommunicationOverviewSvsItem[];
  readonly documentList: CommunicationOverviewDocumentItem[];
  readonly freeTextList: CommunicationOverviewDocumentItem[];
  readonly reportList: CommunicationOverviewDocumentItem[];
  readonly honoraryNoteList: CommunicationOverviewHonoraryNoteItem[];

  readonly rimSelectedIds: number[];
  readonly svsSelectedIds: number[];
  readonly honoraryNoteSelectedIds: number[];
  readonly freeTextSelectedIds: number[];
  readonly documentSelectedIds: number[];
  readonly reportSelectedIds: number[];

  readonly rimNonValidIds: number[];
  readonly svsNonValidIds: number[];

  readonly rimNonValidBusinessList: BusinessResult[];
  readonly svsNonValidBusinessList: BusinessResult[];

  readonly toggleAllRimIsChecked: boolean;
  readonly toggleAllSvsIsChecked: boolean;
  readonly toggleAllFreeTextIsChecked: boolean;
  readonly toggleAllImageIsChecked: boolean;
  readonly toggleAllReportIsChecked: boolean;
  readonly toggleAllDaccaRimIsChecked: boolean;
  readonly toggleAllDaccaSvsIsChecked: boolean;
  readonly toggleAllHonoraryNoteIsChecked: boolean;
  readonly showAskedAmountModal: boolean;
  readonly showDeleteItemsModal: boolean;
  readonly askedAmount: string;
}

export const enum OverviewActionTypes {
  TOGGLE_RIM_ITEM = '@@overview/TOGGLE_RIM_ITEM',
  TOGGLE_ALL_RIM_ITEM = '@@overview/TOGGLE_ALL_RIM_ITEM',
  TOGGLE_SVS_ITEM = '@@overview/TOGGLE_SVS_ITEM',
  TOGGLE_ALL_SVS_ITEM = '@@overview/TOGGLE_ALL_SVS_ITEM',
  TOGGLE_DACCA_RIM_ITEM = '@@overview/TOGGLE_DACCA_RIM_ITEM',
  TOGGLE_ALL_DACCA_RIM_ITEM = '@@overview/TOGGLE_ALL_DACCA_RIM_ITEM',
  TOGGLE_DACCA_SVS_ITEM = '@@overview/TOGGLE_DACCA_SVS_ITEM',
  TOGGLE_ALL_DACCA_SVS_ITEM = '@@overview/TOGGLE_ALL_DACCA_SVS_ITEM',
  TOGGLE_FREE_TEXT_ITEM = '@@overview/TOGGLE_FREE_TEXT_ITEM',
  TOGGLE_ALL_FREE_TEXT_ITEM = '@@overview/TOGGLE_ALL_FREE_TEXT_ITEM',
  TOGGLE_IMAGE_ITEM = '@@overview/TOGGLE_IMAGE_ITEM',
  TOGGLE_ALL_IMAGE_ITEM = '@@overview/TOGGLE_ALL_IMAGE_ITEM',
  TOGGLE_REPORT_ITEM = '@@overview/TOGGLE_REPORT_ITEM',
  TOGGLE_ALL_REPORT_ITEM = '@@overview/TOGGLE_ALL_REPORT_ITEM',
  TOGGLE_HONORARY_NOTE_ITEM = '@@overview/TOGGLE_HONORARY_NOTE_ITEM',
  TOGGLE_ALL_HONORARY_NOTE_ITEM = '@@overview/TOGGLE_ALL_HONORARY_NOTE_ITEM',
  TOGGLE_SELECT_ALL = '@@overview/TOGGLE_SELECT_ALL',

  SEND_ITEMS = '@@overview/SEND_ITEMS',
  SEND_ITEMS_WITH_WARNING = '@@overview/SEND_ITEMS_WITH_WARNING',
  ASKED_AMOUNT_MODAL_OPEN = '@@overview/ASKED_AMOUNT_MODAL_OPEN',
  ASKED_AMOUNT_MODAL_CLOSE = '@@overview/ASKED_AMOUNT_MODAL_CLOSE',
  DELETE_ITEMS = '@@overview/DELETE_ITEMS',
  SEND_ITEMS_SUCCESS = '@@overview/SEND_ITEMS_SUCCESS',
  DELETE_ITEMS_SUCCESS = '@@overview/DELETE_ITEMS_SUCCESS',
  SEND_ITEMS_FAIL = '@@overview/SEND_ITEMS_FAIL',
  DELETE_ITEMS_FAIL = '@@overview/DELETE_ITEMS_FAIL',

  GET_RIM_LIST = '@@overview/GET_RIM_LIST',
  GET_RIM_LIST_SUCCESS = '@@overview/GET_RIM_LIST_SUCCESS',
  GET_RIM_LIST_FAIL = '@@overview/GET_RIM_LIST_FAIL',
  GET_SVS_LIST = '@@overview/GET_SVS_LIST',
  GET_SVS_LIST_SUCCESS = '@@overview/GET_SVS_LIST_SUCCESS',
  GET_SVS_LIST_FAIL = '@@overview/GET_SVS_LIST_FAIL',
  GET_DOCUMENT_LIST = '@@overview/GET_DOCUMENT_LIST',
  GET_DOCUMENT_LIST_SUCCESS = '@@overview/GET_DOCUMENT_LIST_SUCCESS',
  GET_DOCUMENT_LIST_FAIL = '@@overview/GET_DOCUMENT_LIST_FAIL',
  GET_HONORARY_NOTE_LIST = '@@overview/GET_HONORARY_NOTE_LIST',
  GET_HONORARY_NOTE_LIST_SUCCESS = '@@overview/GET_HONORARY_NOTE_LIST_SUCCESS',
  GET_HONORARY_NOTE_LIST_FAIL = '@@overview/GET_HONORARY_NOTE_LIST_FAIL',

  GET_ASKED_AMOUNT = '@@overview/GET_ASKED_AMOUNT_LIST',
  GET_ASKED_AMOUNT_SUCCESS = '@@overview/GET_ASKED_AMOUNT_SUCCESS',
  GET_ASKED_AMOUNT_FAIL = '@@overview/GET_ASKED_AMOUNT_FAIL',

  CHANGE_ASKED_AMOUNT = '@@overview/CHANGE_ASKED_AMOUNT_LIST',
  SET_ASKED_AMOUNT = '@@overview/SET_ASKED_AMOUNT_LIST',
  SET_ASKED_AMOUNT_SUCCESS = '@@overview/SET_ASKED_AMOUNT_SUCCESS',
  SET_ASKED_AMOUNT_FAIL = '@@overview/SET_ASKED_AMOUNT_FAIL',

  SVS_VALIDATE_LIST = '@@overview/SVS_VALIDATE_LIST',
  RIM_VALIDATE_LIST = '@@overview/RIM_VALIDATE_LIST',

  SVS_SET_NON_VALID_ITEMS = '@@overview/SVS_SET_NON_VALID_ITEMS',
  RIM_SET_NON_VALID_ITEMS = '@@overview/RIM_SET_NON_VALID_ITEMS',
  SVS_SET_NON_VALID_BUSINESS_ITEMS = '@@overview/SVS_SET_NON_VALID_BUSINESS_ITEMS',
  RIM_SET_NON_VALID_BUSINESS_ITEMS = '@@overview/RIM_SET_NON_VALID_BUSINESS_ITEMS',

  RIM_STATUS_CHANGE = '@@overview/RIM_STATUS_CHANGE',
  RIM_STATUS_CHANGE_SUCCESS = '@@overview/RIM_STATUS_CHANGE_SUCCESS',
  RIM_STATUS_CHANGE_FAIL = '@@overview/RIM_STATUS_CHANGE_FAIL',

  GENERATE_REPORTS_RIM = '@@overview/GENERATE_REPORTS_RIM',
  GENERATE_REPORTS_RIM_SUCCESS = '@@overview/GENERATE_REPORTS_RIM_SUCCESS',
  GENERATE_REPORTS_RIM_FAIL = '@@overview/GENERATE_REPORTS_RIM_FAIL',

  GENERATE_REPORTS_SVS = '@@overview/GENERATE_REPORTS_SVS',
  GENERATE_REPORTS_SVS_SUCCESS = '@@overview/GENERATE_REPORTS_SVS_SUCCESS',
  GENERATE_REPORTS_SVS_FAIL = '@@overview/GENERATE_REPORTS_SVS_FAIL',

  DELETE_OVERVIEW_MODAL_OPEN = '@@overview/DELETE_OVERVIEW_MODAL_OPEN',
  DELETE_OVERVIEW_MODAL_CLOSE = '@@overview/DELETE_OVERVIEW_MODAL_CLOSE',
}
